<script setup lang="ts">
/**
 * The agent's reasoning steps, ported from the live viewer's StepTimeline.
 *
 * Structure and styling are kept; the type moves from `TimelineStep` to the
 * canonical `ReasoningStep`, and the collapse behavior is new. The live viewer
 * always expands because the timeline *is* its page — in a chat bubble that
 * would bury the answer under the working-out, so this collapses once the turn
 * settles and auto-expands while the turn is still streaming (you want to watch
 * it happen; you don't want to scroll past it afterwards).
 */
import { computed, ref, watch } from 'vue';

import type { ReasoningStep } from '../types/message';

const props = defineProps<{
  steps: readonly ReasoningStep[];
  streaming?: boolean;
}>();

const userToggled = ref(false);
const open = ref(props.streaming ?? false);

// Follow the stream until the reader takes over; then their choice sticks. A
// block that snaps shut under someone mid-read is worse than one left open.
watch(
  () => props.streaming,
  (streaming) => {
    if (!userToggled.value) open.value = !!streaming;
  },
);

function toggle() {
  userToggled.value = true;
  open.value = !open.value;
}

const kindLabel: Record<ReasoningStep['kind'], string> = {
  thought: 'Thinking',
  tool_call: 'Tool call',
  tool_result: 'Tool result',
  handoff: 'Handoff',
};

const kindClass: Record<ReasoningStep['kind'], string> = {
  thought: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  tool_call: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  tool_result: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  handoff: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
};

const summary = computed(() => {
  const n = props.steps.length;
  return `${n} step${n === 1 ? '' : 's'}`;
});

function formatDuration(ms?: number): string {
  if (!ms) return '';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function formatTokens(step: ReasoningStep): string {
  const parts: string[] = [];
  if (step.usage?.inputTokens) parts.push(`${step.usage.inputTokens} in`);
  if (step.usage?.outputTokens) parts.push(`${step.usage.outputTokens} out`);
  return parts.join(' · ');
}
</script>

<template>
  <div data-testid="thinking-block" class="rounded-lg border border-gray-200 dark:border-gray-700">
    <button
      type="button"
      data-testid="thinking-toggle"
      class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      :aria-expanded="open"
      @click="toggle"
    >
      <span class="transition-transform" :class="open ? 'rotate-90' : ''" aria-hidden="true">›</span>
      <span>{{ streaming ? 'Thinking…' : 'Reasoning' }}</span>
      <span class="text-gray-400">{{ summary }}</span>
    </button>

    <ol v-if="open" data-testid="thinking-steps" class="space-y-2 px-3 pb-3">
      <li
        v-for="step in steps"
        :key="step.id"
        class="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
            :class="kindClass[step.kind]"
            >{{ kindLabel[step.kind] }}</span
          >
          <span v-if="step.toolName" class="font-mono text-xs text-gray-600 dark:text-gray-300">{{
            step.toolName
          }}</span>
          <span
            v-if="formatDuration(step.usage?.durationMs)"
            class="ml-auto text-[10px] text-gray-400 dark:text-gray-500"
          >
            {{ formatDuration(step.usage?.durationMs) }}
          </span>
          <span v-if="formatTokens(step)" class="text-[10px] text-gray-400 dark:text-gray-500">
            {{ formatTokens(step) }}
          </span>
        </div>
        <!-- Interpolated, never v-html: step content is model output, and it may
             be the literal "[REDACTED]", which renders verbatim by doing nothing. -->
        <p
          class="mt-1 whitespace-pre-wrap break-words font-mono text-sm text-gray-800 dark:text-gray-200"
        >
          {{ step.content }}
        </p>
      </li>
    </ol>
  </div>
</template>
